import React, { useCallback } from 'react';
import { useDarkMode } from '../../../context/DarkModeContext';

interface TimelineControlsProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  isWindowLocked?: boolean;
}

const TimelineControls: React.FC<TimelineControlsProps> = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onReset,
  isWindowLocked = false
}) => {
  const { darkMode } = useDarkMode();
  const canZoomIn = zoomLevel < 100 && !isWindowLocked;
  const canZoomOut = zoomLevel > 0 && !isWindowLocked;

  // Tailwind classes for buttons - updated for dark theme
  const buttonBaseClasses = `
    flex items-center justify-center
    rounded
    transition-colors duration-200
    min-w-[32px] min-h-[32px]
    p-2
    relative
    focus:outline-none
    focus:ring-2
    focus:ring-offset-2
    focus:ring-blue-500
  `;

  // Always use dark styling since the timeline now has a dark background

  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={onZoomIn}
        disabled={!canZoomIn}
        className={`${buttonBaseClasses} bg-gray-700 text-white hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50`}
        aria-label="Zoom in"
        title={isWindowLocked ? "Zoom disabled while window size is locked" : "Zoom in to see more detail"}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
      </button>

      <button
        onClick={onZoomOut}
        disabled={!canZoomOut}
        className={`${buttonBaseClasses} bg-gray-700 text-white hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50`}
        aria-label="Zoom out"
        title={isWindowLocked ? "Zoom disabled while window size is locked" : "Zoom out to see more events"}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      </button>

      <button
        onClick={onReset}
        className={`${buttonBaseClasses} bg-gray-700 text-white hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50`}
        aria-label="Reset view"
        title="Reset timeline to show all events"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      <div className="text-xs text-gray-300 ml-2">
        Zoom: {isWindowLocked ? `${Math.round(zoomLevel)}% (Locked)` : canZoomIn && canZoomOut ? `${Math.round(zoomLevel)}%` : zoomLevel === 0 ? 'Min' : 'Max'}
      </div>
    </div>
  );
};

export default TimelineControls; 